/**
 * Normaliza payload de eventos Memed para contrato interno (T9).
 */

import type { MemedPrescriptionResult } from './memed.types';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function pickString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return undefined;
}

function dig(root: Record<string, unknown>, path: string[]): unknown {
  let current: unknown = root;
  for (const key of path) {
    const rec = asRecord(current);
    if (!rec) return undefined;
    current = rec[key];
  }
  return current;
}

function firstFromArray(value: unknown): Record<string, unknown> | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  return asRecord(value[0]);
}

/** Extrai ID de URLs digitais comuns da Memed. */
export function extractMemedIdFromLink(link?: string | null): string | undefined {
  if (!link || typeof link !== 'string') return undefined;
  const trimmed = link.trim();
  if (!trimmed) return undefined;

  const patterns = [
    /\/prescricoes\/([^/?#]+)/i,
    /\/r\/([^/?#]+)/i,
    /[?&](?:id|prescriptionId|prescricaoId)=([^&#]+)/i,
    /\/p\/([^/?#]+)/i,
  ];
  for (const pattern of patterns) {
    const match = trimmed.match(pattern);
    if (match?.[1]) {
      try {
        return decodeURIComponent(match[1]).trim();
      } catch {
        return match[1].trim();
      }
    }
  }
  return undefined;
}

/**
 * Heurística: payload parece receita emitida (para sniffer de eventos).
 * Exige ID + (link ou shape de receita). Status sozinho não basta.
 */
export function looksLikeMemedPrescriptionPayload(raw: unknown): boolean {
  if (typeof raw === 'string' || typeof raw === 'number') {
    const value = String(raw).trim();
    // Evita disparar sniffer com tokens/strings curtas genéricas.
    return value.length >= 4 && !/\s/.test(value);
  }
  const root = asRecord(raw) || firstFromArray(raw);
  if (!root) return false;

  const link = pickString(
    root.link,
    root.url,
    root.digitalLink,
    root.link_digital,
    dig(root, ['data', 'attributes', 'link']),
  );
  const id = pickString(
    root.id,
    root.uuid,
    root.prescricaoId,
    root.prescriptionId,
    root.providerDocumentId,
    dig(root, ['data', 'id']),
    extractMemedIdFromLink(link),
  );
  if (!id) return false;

  const hasLink = Boolean(link);
  const hasPrescriptionShape = Boolean(
    root.prescricoes ||
      root.prescricao ||
      root.medicamentos ||
      root.prescription ||
      dig(root, ['data', 'type']) === 'prescricoes' ||
      dig(root, ['data', 'attributes', 'medicamentos']),
  );

  return hasLink || hasPrescriptionShape;
}

/**
 * Extrai apenas metadados seguros do evento de emissão.
 * Não espalha o payload cru para a UI.
 */
export function normalizeMemedPrescriptionResult(
  raw: unknown,
  eventName?: string,
): MemedPrescriptionResult {
  // Algumas builds enviam só o ID.
  if (typeof raw === 'string' || typeof raw === 'number') {
    const asText = String(raw).trim();
    const fromLink = extractMemedIdFromLink(asText);
    const id = fromLink || (asText.includes('/') ? undefined : pickString(asText));
    const result: MemedPrescriptionResult = {
      status: 'issued',
      rawEventName: eventName,
    };
    if (id) result.providerDocumentId = id;
    if (fromLink || /^https?:\/\//i.test(asText)) result.digitalLink = asText;
    return result;
  }

  const root = asRecord(raw) || firstFromArray(raw) || {};

  const data = asRecord(root.data) || {};
  const attributes =
    asRecord(data.attributes) || asRecord(root.attributes) || {};
  const firstPrescription =
    firstFromArray(root.prescricoes) ||
    asRecord(root.prescricao) ||
    firstFromArray(dig(root, ['data', 'prescricoes'])) ||
    asRecord(dig(root, ['prescription'])) ||
    null;

  const digitalLink = pickString(
    root.digitalLink,
    root.link,
    root.url,
    root.linkDigital,
    root.link_digital,
    root.digital_link,
    attributes.digitalLink,
    attributes.link,
    attributes.url,
    attributes.link_digital,
    firstPrescription?.digitalLink,
    firstPrescription?.link,
    firstPrescription?.url,
    dig(root, ['prescription', 'digitalLink']),
    dig(root, ['data', 'attributes', 'link']),
  );

  const providerDocumentId = pickString(
    root.id,
    root.uuid,
    root.prescricaoId,
    root.prescriptionId,
    root.providerDocumentId,
    root.prescription_id,
    root.idPrescricao,
    data.id,
    attributes.id,
    attributes.uuid,
    firstPrescription?.id,
    firstPrescription?.uuid,
    dig(root, ['prescription', 'id']),
    dig(root, ['prescription', 'uuid']),
    dig(root, ['data', 'id']),
    extractMemedIdFromLink(digitalLink),
  );

  const status =
    pickString(root.status, attributes.status, firstPrescription?.status) ||
    'issued';

  const result: MemedPrescriptionResult = {
    status,
    rawEventName: eventName,
  };
  if (providerDocumentId) result.providerDocumentId = providerDocumentId;
  if (digitalLink) result.digitalLink = digitalLink;
  return result;
}
